import { call, put } from "typed-redux-saga";
import { CouponCheckResponse } from "../../core/api/requests/coupon/check/types";
import { OrderFinalizeResponse } from "../../core/api/requests/order/finalize/types";
import { OrderSearchResponse } from "../../core/api/requests/order/search/types";
import { OrderTemporaryResponse } from "../../core/api/requests/order/temporary/types";
import {
  couponCheckApi,
  orderFinalizeApi,
  orderSearchApi,
  orderStatusApi,
  orderTemporaryApi,
} from "../../core/http/httpClient";
import { ApiResponse } from "../../core/types";
import { getOrderHistoryStatus } from "../../utils/order";
import { takeEveryFsa } from "../operations";
import { orderActions } from "./actions";
import { OrderHistory } from "./types";

export function* orderSaga() {
  yield takeEveryFsa(orderActions.getOrderId.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<OrderTemporaryResponse> = yield call(
      () => {
        return orderTemporaryApi
          .call(body)
          .then((response) => ({ response }))
          .catch((error) => ({ error }));
      },
    );

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      orderActions.getOrderId.done({
        params: action.payload,
        result: response.data,
      }),
    );
    onSuccess?.({ orderId: response.data.order_id });
  });

  yield takeEveryFsa(orderActions.checkOrderItems.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<CouponCheckResponse> = yield call(
      () => {
        return couponCheckApi
          .call(body)
          .then((response) => ({ response }))
          .catch((error) => ({ error }));
      },
    );

    onComplete?.();

    if (!response || error) return onError?.(error);

    const orderedMenus = response.data.menus.map((menu) => {
      const isEarlyBird = body.early_bird === 1 && !!menu.coupon ? true : false;
      return {
        id: menu.id,
        name: menu.name,
        code: menu.code,
        price: menu.price_with_tax,
        quantity: menu.quantity,
        options: menu.option,
        coupon: menu.coupon && {
          message: menu.coupon.message[0].message,
          discount: menu.coupon.message[0].discount,
          type: menu.coupon.message[0].type,
        },
        isEarlyBird: isEarlyBird,
      };
    });

    const orderContent = {
      menus: orderedMenus,
      totalQuantity: response.data.order_quantity,
      totalTaxAmount: response.data.coupon
        ? response.data.coupon.tax
        : response.data.tax,
      totalPriceWithTax: response.data.coupon
        ? response.data.coupon.message[0]
          ? response.data.subtotal_with_tax
          : response.data.coupon.total_with_tax
        : response.data.total_with_tax,
      coupon: response.data.coupon &&
        response.data.coupon.message[0] && {
          message: response.data.coupon.message[0].message,
          discount: response.data.coupon.message[0].discount,
          price: response.data.coupon.total_with_tax,
          tax: response.data.coupon.tax,
          type: response.data.coupon.message[0].type,
        },
    };

    yield* put(
      orderActions.checkOrderItems.done({
        params: action.payload,
        result: orderContent,
      }),
    );
    onSuccess?.(orderContent);
  });

  yield takeEveryFsa(
    orderActions.updateOrderStatus.started,
    function* (action) {
      const { onComplete, onError, onSuccess, ...body } = action.payload;
      const { response, error }: ApiResponse<{}> = yield call(() => {
        return orderStatusApi
          .call(body)
          .then((response) => ({ response }))
          .catch((error) => ({ error }));
      });

      onComplete?.();

      if (!response || error) return onError?.(error);

      yield* put(
        orderActions.updateOrderStatus.done({
          params: action.payload,
          result: {},
        }),
      );
      onSuccess?.({});
    },
  );

  yield takeEveryFsa(orderActions.createOrder.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<OrderFinalizeResponse> = yield call(
      () => {
        return orderFinalizeApi
          .call(body)
          .then((response) => ({ response }))
          .catch((error) => ({ error }));
      },
    );

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      orderActions.createOrder.done({
        params: action.payload,
        result: response.data,
      }),
    );
    onSuccess?.(response.data);
  });

  yield takeEveryFsa(orderActions.getOrderHistory.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<OrderSearchResponse> = yield call(
      () => {
        return orderSearchApi
          .call(body)
          .then((response) => ({ response }))
          .catch((error) => ({ error }));
      },
    );

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      orderActions.getOrderHistory.done({
        params: action.payload,
        result: response.data.orders.map<OrderHistory>((order) => ({
          id: order.id,
          status: getOrderHistoryStatus(order.status.code),
          quantity: order.order_quantity,
          subtotalPriceWithTax: order.subtotal_with_tax,
          totalPriceWithTax: order.coupon
            ? order.coupon.message[0]
              ? order.subtotal_with_tax
              : order.coupon.total_with_tax
            : order.total_with_tax,
          taxAmount: order.coupon ? order.coupon.tax : order.tax,
          orderDate: order.order_date,
          pickupDate: order.pickup_date,
          menus: order.menus.map((menu) => ({
            id: menu.id,
            name: menu.name,
            code: menu.code,
            price: menu.price_with_tax,
            quantity: menu.quantity,
            options: menu.option.map((option) => ({
              id: option.id,
              name: option.name,
            })),
            coupon: menu.coupon && {
              discount: menu.coupon.message[0].discount,
              message: menu.coupon.message[0].message,
              type: menu.coupon.message[0].type,
            },
            isEarlyBird: order.is_early_bird ?? false,
          })),
          store: {
            id: order.store.id,
            name: order.store.name,
            address: order.store.address,
            city: order.store.city,
            prefecture: order.store.prefecture,
          },
          isReservation: order.reservation,
          isAdvancedPayment: order.is_advanced_payment,
          isAutoOrderError: order.is_auto_order_error,
          isEarlyBird: order.is_early_bird ?? false,
          memo: order.memo,
          coupon: order.coupon &&
            order.coupon.message[0] && {
              discount: order.coupon.message[0].discount,
              message: order.coupon.message[0].message,
              price: order.coupon.total_with_tax,
              tax: order.coupon.tax,
              type: order.coupon.message[0].type,
            },
        })),
      }),
    );
    onSuccess?.(response.data);
  });
}
